import { useEffect, useState, useRef, MutableRefObject } from 'react';
import { IRouteData } from '../routing/routing.types';
import { Breadcrumbs as MuiBreadcrumbs, Grid } from '@mui/material';
import { matchPath, useLocation, Link } from 'react-router';
import { BreadcrumbItem } from './BreadcrumbsItem';
import { IComponentCss } from '@tap/mui-ui';
import { useIsOverflow } from '../hooks/useIsOverflow';
import { DropdownMenuOpenButton, DropdownMenu } from '@tap/mui-ui';
import HomeIcon from '@mui/icons-material/Home';
import { AppInstancesSettingCode, DocumentCode } from '@tap/data-providers';

/*
*   Хлебные крошки
*/

export type IBreadcrumbsItem = {
    isHome?: boolean,
    label: string,
    customRenderLabel?: any,
    link: string,
    includeParamNameInBreadcrumbs?: boolean,
    hideBreadcrumbs?: boolean,
    isLast?: boolean,
}


const css: IComponentCss = {
    breadcrumb: {
        marginLeft: '9px',
        color: '#666666',
        fontSize: '0.875em',
        lineHeight: 1,
    },
    breadcrumbFull: {
        height: '16px',
        overflowY: 'auto',
    },
    homeIcon: {
        marginRight: '4px',
        marginBottom: '1px',
    },
    threeDotsButton: {
        backgroundColor: '#F2F2F2',
        height: '16px',
    },
}


function getBreadcrumbs(pathname: string, routes: IRouteData): IBreadcrumbsItem[] {
    const breadcrumbArray: IBreadcrumbsItem[] = [];
    let pathnameArray = pathname.split('/').filter(p => p);

    // Преобразуем массив по принципу: предыдущий элемент + текущий для корректного отображения хлебных крошек
    // прим: arr[0] = '/s', arr[1] = 's/b', arr[2] = '/s/b/x'.
    pathnameArray = pathnameArray.map((elem, i) => pathnameArray.slice(0, i + 1).reduce((p: string, c: string) => p + '/' + c));
    pathnameArray.forEach((path, index) => {
        Object.values(routes).forEach(route => {
            const match = matchPath(route.path.replace(/[?]/g, ''), '/' + path)
            if (
                !!breadcrumbArray && match
                && route.path !== routes.notFound.path
            ) {
                if (route.hideBreadcrumbs === true)
                    return [];

                switch (route.labelTypeInBreadcrumbs) {
                    case 'ID': {
                        const pathArray = path.split('/').filter(p => p);
                        breadcrumbArray.push({
                            label: decodeURIComponent(pathArray[pathArray.length - 1]).split(',').join(', '),
                            link: path,
                        });
                        break;
                    }
                    case 'SETTING_ID': {
                        const pathArray = path.split('/').filter(p => p);
                        const lastSegment = pathArray[pathArray.length - 1];

                        switch (lastSegment) {
                            case AppInstancesSettingCode.Scada:
                                breadcrumbArray.push({
                                    label: 'Настройки Scada сервис',
                                    link: path,
                                });
                                break;
                            case AppInstancesSettingCode.Shell:
                                breadcrumbArray.push({
                                    label: 'Настройки Shell',
                                    link: path,
                                });
                                break;
                            default:
                                breadcrumbArray.push({
                                    label: decodeURIComponent(lastSegment).split(',').join(', '),
                                    link: path,
                                });
                                break;
                        }
                        break;
                    }
                    case 'ERROR_LOG_PAGE': {
                        const pathArray = path.split('/').filter(p => p);
                        if (pathArray[pathArray.length - 1] === 'log-explorer') {
                            breadcrumbArray.push({
                                label: 'Логи',
                                link: path,
                            });
                        } else {
                            breadcrumbArray.push({
                                label: decodeURIComponent(pathArray[pathArray.length - 1]).split(',').join(', '),
                                link: path,
                            });
                        }
                        break;
                    }
                    case 'EXCLUDE_LAST_SECTION_PATH':
                        if (index !== pathnameArray.length - 1) {
                            const pathArray1 = path.split('/').filter(p => p);
                            breadcrumbArray.push({
                                label: decodeURIComponent(pathArray1[pathArray1.length - 1]).split(',').join(', '),
                                link: path,
                            });
                        }
                        break;
                    case 'EXCLUDE_LAST_ID': {
                        const pathArray1 = path.split('/').filter(p => p);
                        breadcrumbArray.push({
                            label: decodeURIComponent(pathArray1[pathArray1.length - 2]).split(',').join(', '),
                            link: path,
                        });
                        break;
                    }
                    case 'DOCUMENT_DETAILS_PAGE': {
                        const codes = Object.values(DocumentCode);
                        const documentKey = match?.params?.documentKey;
                        const code = documentKey?.split('-')[0];
                        if (codes.includes(code)) {
                            breadcrumbArray.push({
                                label: documentKey || route.label,
                                link: path,
                            });
                        }
                        break;
                    }
                    default:
                        breadcrumbArray.push({
                            label: route.label,
                            link: path,
                            isHome: route.isHome,
                        });
                }
            }
        });
    });

    if (breadcrumbArray.length > 0) breadcrumbArray[breadcrumbArray.length - 1].isLast = true;

    return breadcrumbArray;
}

export type IBreadcrumbsProps = {
    routes: IRouteData,
}

export function Breadcrumbs({ routes }: IBreadcrumbsProps) {
    const ref = useRef() as MutableRefObject<HTMLDivElement>;
    const { pathname } = useLocation();
    const [breadcrumbArray, setBreadcrumbArray] = useState<IBreadcrumbsItem[]>([])
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const isOverflow = useIsOverflow(ref, () => { });
    const open = Boolean(anchorEl);

    useEffect(() => {
        setBreadcrumbArray(getBreadcrumbs(pathname, routes))
    }, [routes, pathname]);

    return (
        <>
            {!!breadcrumbArray && breadcrumbArray.length > 0 &&
                <Grid item container>
                    {/* Хлебные крошки: развернутые */}
                    <MuiBreadcrumbs
                        ref={ref}
                        aria-label='breadcrumb'
                        sx={{
                            ...css.breadcrumb,
                            ...css.breadcrumbFull,
                            visibility: isOverflow === false ? 'visible' : 'hidden',
                            marginTop: isOverflow === false ? 0 : -20,
                        }}
                    >
                        {/* Главная */}
                        {breadcrumbArray.filter(bi => !bi.isHome).length > 0 && (
                            <BreadcrumbItem
                                label={routes.home.label}
                                link={routes.home.path}
                                icon={<HomeIcon sx={css.homeIcon} fontSize='inherit' />}
                            />
                        )}


                        {/* Остальные ссылки */}
                        {breadcrumbArray.filter(bi => !bi.isHome).map((breadcrumb, i) =>
                            <BreadcrumbItem
                                key={i}
                                label={breadcrumb.label}
                                customRenderLabel={breadcrumb.customRenderLabel}
                                link={breadcrumb.link}
                                isDisableLink={breadcrumb?.isLast || breadcrumb?.includeParamNameInBreadcrumbs}
                            />,
                        )}
                    </MuiBreadcrumbs>

                    {/* Хлебные крошки: компактные */}
                    <MuiBreadcrumbs
                        aria-label='breadcrumb-compact'
                        sx={{
                            ...css.breadcrumb,
                            display: isOverflow === true ? 'flex' : 'none',
                        }}
                    >
                        {/* Главная */}
                        <BreadcrumbItem
                            label={routes.home.label}
                            link={routes.home.path}
                            icon={<HomeIcon sx={css.homeIcon} fontSize='inherit' />}
                            isDisableLink={breadcrumbArray.length === 0}
                        />

                        {/* Остальные ссылки */}
                        {breadcrumbArray.length > 1 &&
                            <Grid item container>
                                <DropdownMenuOpenButton
                                    primaryText={'...'}
                                    sxButton={css.threeDotsButton}
                                    handleClick={e => setAnchorEl(e.currentTarget)}
                                />

                                <DropdownMenu
                                    id='breadcrumb-menu-compact'
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={() => setAnchorEl(null)}
                                    menuItems={
                                        breadcrumbArray.filter(bi => !bi.isHome).map((breadcrumb: IBreadcrumbsItem) => {
                                            return ({
                                                onClick: () => setAnchorEl(null),
                                                to: breadcrumb.link,
                                                label: breadcrumb.customRenderLabel
                                                    ? breadcrumb.customRenderLabel
                                                    : breadcrumb.label ? breadcrumb.label : undefined,
                                                component: Link,
                                            })
                                        }).slice(0, breadcrumbArray.length - 1)
                                    }
                                />
                            </Grid>
                        }

                        {/* Последняя ссылка (текущая страница) */}
                        <BreadcrumbItem
                            label={
                                breadcrumbArray[breadcrumbArray.length - 1].customRenderLabel
                                    ? breadcrumbArray[breadcrumbArray.length - 1].customRenderLabel
                                    : breadcrumbArray[breadcrumbArray.length - 1].label
                            }
                            link={breadcrumbArray[breadcrumbArray.length - 1].link}
                            isDisableLink={true}
                        />
                    </MuiBreadcrumbs>
                </Grid>
            }
        </>
    );
}

